import { useState, useEffect } from 'react';
import {
  Phone,
  Users,
  Clock,
  Headphones,
  UserCheck,
  Activity,
  RefreshCw,
  AlertCircle,
  Settings,
} from 'lucide-react';
import { useLiveQueueMetrics } from '../../hooks/useConnect';

// Default configuration - can be overridden via environment or settings
const DEFAULT_CONFIG = {
  instanceId: import.meta.env.VITE_CONNECT_INSTANCE_ID || '',
  region: import.meta.env.VITE_CONNECT_REGION || 'us-east-1',
  queueIds: (import.meta.env.VITE_CONNECT_QUEUE_IDS || '').split(',').filter(Boolean),
  refreshInterval: 15000, // 15 seconds
};

// Queue name mapping (can be configured)
const QUEUE_NAMES = {
  // Add your queue ID to name mappings here
  // 'queue-uuid-1': 'Main Support',
  // 'queue-uuid-2': 'Sales',
};

function MetricCard({ icon: Icon, label, value, subValue, color = 'amber', loading }) {
  const colorClasses = {
    amber: 'bg-amber-50 text-amber-600 border-amber-200',
    green: 'bg-green-50 text-green-600 border-green-200',
    blue: 'bg-blue-50 text-blue-600 border-blue-200',
    red: 'bg-red-50 text-red-600 border-red-200',
    gray: 'bg-gray-50 text-gray-600 border-gray-200',
  };

  return (
    <div className={`rounded-lg border p-4 ${colorClasses[color]}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4" />
        <span className="text-sm font-medium">{label}</span>
      </div>
      {loading ? (
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-16"></div>
        </div>
      ) : (
        <>
          <p className="text-2xl font-bold">{value}</p>
          {subValue && <p className="text-xs opacity-75 mt-1">{subValue}</p>}
        </>
      )}
    </div>
  );
}

function QueueCard({ queue, queueName, loading }) {
  const formatDuration = (seconds) => {
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const getQueueColor = (count) => {
    if (count === 0) return 'green';
    if (count <= 3) return 'amber';
    if (count <= 10) return 'orange';
    return 'red';
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
      <div className="bg-gradient-to-r from-amber-50 to-orange-50 px-4 py-3 border-b border-amber-100">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <Headphones className="w-4 h-4 text-amber-600" />
            {queueName}
          </h3>
          {!loading && (
            <span className={`text-xs px-2 py-0.5 rounded-full ${
              queue.contacts_in_queue === 0
                ? 'bg-green-100 text-green-700'
                : queue.contacts_in_queue <= 5
                ? 'bg-amber-100 text-amber-700'
                : 'bg-red-100 text-red-700'
            }`}>
              {queue.contacts_in_queue === 0 ? 'Clear' : `${queue.contacts_in_queue} waiting`}
            </span>
          )}
        </div>
      </div>

      <div className="p-4">
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
          <MetricCard
            icon={Phone}
            label="In Queue"
            value={queue.contacts_in_queue}
            color={getQueueColor(queue.contacts_in_queue)}
            loading={loading}
          />
          <MetricCard
            icon={Clock}
            label="Longest Wait"
            value={formatDuration(queue.oldest_contact_age_seconds)}
            color={queue.oldest_contact_age_seconds > 120 ? 'red' : 'gray'}
            loading={loading}
          />
          <MetricCard
            icon={UserCheck}
            label="Available"
            value={queue.agents_available}
            color={queue.agents_available > 0 ? 'green' : 'red'}
            loading={loading}
          />
          <MetricCard
            icon={Headphones}
            label="On Call"
            value={queue.agents_on_contact}
            color="blue"
            loading={loading}
          />
          <MetricCard
            icon={Users}
            label="Online"
            value={queue.agents_online}
            color="gray"
            loading={loading}
          />
          <MetricCard
            icon={Activity}
            label="Utilization"
            value={
              queue.agents_online > 0
                ? `${Math.round((queue.agents_on_contact / queue.agents_online) * 100)}%`
                : 'N/A'
            }
            subValue={`${queue.agents_on_contact}/${queue.agents_online} agents`}
            color="amber"
            loading={loading}
          />
        </div>
      </div>
    </div>
  );
}

function ConfigPanel({ config, onConfigChange, onClose }) {
  const [localConfig, setLocalConfig] = useState(config);

  const handleSave = () => {
    onConfigChange(localConfig);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Configure Live Calls</h2>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Connect Instance ID
            </label>
            <input
              type="text"
              value={localConfig.instanceId}
              onChange={(e) => setLocalConfig({ ...localConfig, instanceId: e.target.value })}
              placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Queue IDs (comma-separated)
            </label>
            <textarea
              value={localConfig.queueIds.join(',')}
              onChange={(e) =>
                setLocalConfig({
                  ...localConfig,
                  queueIds: e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
                })
              }
              placeholder="queue-id-1,queue-id-2"
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              AWS Region
            </label>
            <select
              value={localConfig.region}
              onChange={(e) => setLocalConfig({ ...localConfig, region: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
            >
              <option value="us-east-1">US East (N. Virginia)</option>
              <option value="us-west-2">US West (Oregon)</option>
              <option value="eu-west-2">Europe (London)</option>
              <option value="ap-southeast-2">Asia Pacific (Sydney)</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Refresh Interval (seconds)
            </label>
            <input
              type="number"
              min="5"
              max="300"
              value={localConfig.refreshInterval / 1000}
              onChange={(e) =>
                setLocalConfig({
                  ...localConfig,
                  refreshInterval: parseInt(e.target.value, 10) * 1000,
                })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
            />
          </div>
        </div>
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3 rounded-b-lg">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-lg"
          >
            Save Configuration
          </button>
        </div>
      </div>
    </div>
  );
}

export default function LiveCalls() {
  const [config, setConfig] = useState(() => {
    // Try to load from localStorage
    const saved = localStorage.getItem('liveCalls.config');
    if (saved) {
      try {
        return { ...DEFAULT_CONFIG, ...JSON.parse(saved) };
      } catch {
        return DEFAULT_CONFIG;
      }
    }
    return DEFAULT_CONFIG;
  });
  const [showConfig, setShowConfig] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  const isConfigured = config.instanceId && config.queueIds.length > 0;

  const {
    data: metricsData,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
  } = useLiveQueueMetrics(config.instanceId, config.queueIds, config.region, {
    refetchInterval: config.refreshInterval,
    enabled: isConfigured,
  });

  const metrics = metricsData?.data;

  useEffect(() => {
    if (metrics?.timestamp_utc) {
      setLastUpdated(new Date(metrics.timestamp_utc));
    }
  }, [metrics?.timestamp_utc]);

  const handleConfigChange = (newConfig) => {
    setConfig(newConfig);
    localStorage.setItem('liveCalls.config', JSON.stringify(newConfig));
  };

  // Calculate totals across all queues
  const totals = metrics?.queues?.reduce(
    (acc, queue) => ({
      contacts_in_queue: acc.contacts_in_queue + queue.contacts_in_queue,
      agents_available: acc.agents_available + queue.agents_available,
      agents_on_contact: acc.agents_on_contact + queue.agents_on_contact,
      agents_online: acc.agents_online + queue.agents_online,
      oldest_contact_age_seconds: Math.max(acc.oldest_contact_age_seconds, queue.oldest_contact_age_seconds),
    }),
    {
      contacts_in_queue: 0,
      agents_available: 0,
      agents_on_contact: 0,
      agents_online: 0,
      oldest_contact_age_seconds: 0,
    }
  ) || {
    contacts_in_queue: 0,
    agents_available: 0,
    agents_on_contact: 0,
    agents_online: 0,
    oldest_contact_age_seconds: 0,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Phone className="w-6 h-6 text-amber-500" />
            Live Calls
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Real-time call center queue metrics from AWS Connect
          </p>
        </div>
        <div className="flex items-center gap-3">
          {lastUpdated && (
            <span className="text-xs text-gray-500">
              Updated: {lastUpdated.toLocaleTimeString()}
            </span>
          )}
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={() => setShowConfig(true)}
            className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
          >
            <Settings className="w-4 h-4" />
            Configure
          </button>
        </div>
      </div>

      {/* Not configured state */}
      {!isConfigured && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 text-center">
          <Settings className="w-10 h-10 mx-auto mb-3 text-amber-500" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Configuration Required</h3>
          <p className="text-sm text-gray-600 mb-4">
            Please configure your AWS Connect instance ID and queue IDs to view live metrics.
          </p>
          <button
            onClick={() => setShowConfig(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700"
          >
            <Settings className="w-4 h-4" />
            Configure Now
          </button>
        </div>
      )}

      {/* Error state */}
      {isConfigured && isError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-medium text-red-900">Failed to fetch metrics</h4>
            <p className="text-sm text-red-700 mt-1">
              {error?.response?.data?.message || error?.message || 'Unknown error occurred'}
            </p>
          </div>
        </div>
      )}

      {/* Summary cards */}
      {isConfigured && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <MetricCard
            icon={Phone}
            label="Total In Queue"
            value={totals.contacts_in_queue}
            color={totals.contacts_in_queue === 0 ? 'green' : totals.contacts_in_queue <= 5 ? 'amber' : 'red'}
            loading={isLoading}
          />
          <MetricCard
            icon={Clock}
            label="Max Wait Time"
            value={
              totals.oldest_contact_age_seconds < 60
                ? `${totals.oldest_contact_age_seconds}s`
                : `${Math.floor(totals.oldest_contact_age_seconds / 60)}m`
            }
            color={totals.oldest_contact_age_seconds > 120 ? 'red' : 'gray'}
            loading={isLoading}
          />
          <MetricCard
            icon={UserCheck}
            label="Agents Available"
            value={totals.agents_available}
            color={totals.agents_available > 0 ? 'green' : 'red'}
            loading={isLoading}
          />
          <MetricCard
            icon={Headphones}
            label="Agents On Call"
            value={totals.agents_on_contact}
            color="blue"
            loading={isLoading}
          />
          <MetricCard
            icon={Users}
            label="Agents Online"
            value={totals.agents_online}
            color="gray"
            loading={isLoading}
          />
        </div>
      )}

      {/* Queue cards */}
      {isConfigured && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {isLoading ? (
            // Loading skeleton
            Array.from({ length: config.queueIds.length || 2 }).map((_, i) => (
              <QueueCard
                key={i}
                queue={{
                  contacts_in_queue: 0,
                  oldest_contact_age_seconds: 0,
                  agents_available: 0,
                  agents_on_contact: 0,
                  agents_online: 0,
                }}
                queueName={`Queue ${i + 1}`}
                loading={true}
              />
            ))
          ) : (
            metrics?.queues?.map((queue) => (
              <QueueCard
                key={queue.queue_id}
                queue={queue}
                queueName={QUEUE_NAMES[queue.queue_id] || `Queue ${queue.queue_id.slice(0, 8)}...`}
                loading={false}
              />
            ))
          )}
        </div>
      )}

      {/* Auto-refresh indicator */}
      {isConfigured && !isLoading && (
        <div className="flex items-center justify-center gap-2 text-xs text-gray-400">
          <Activity className="w-3 h-3" />
          Auto-refreshing every {config.refreshInterval / 1000} seconds
        </div>
      )}

      {/* Config panel */}
      {showConfig && (
        <ConfigPanel
          config={config}
          onConfigChange={handleConfigChange}
          onClose={() => setShowConfig(false)}
        />
      )}
    </div>
  );
}
